import React from 'react';
import { TFunction, useTranslation } from '@wix/yoshi-flow-editor';
import { TagName } from '../../../../utils/tagName.const';
import { DateAndTimeDataHooks } from './dataHooks';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import Text, { TextType } from '../../Text/Text';
import { useEditorContext } from '../../Hooks/useEditorContext';
import { classes } from './DateAndTime.st.css';
import { Service } from '../../../../utils/mappers/service.mapper';
import { ServiceType } from '@wix/bookings-uou-types';
import { FormNestedSlot } from '../../../../types/formSelectedSlots';

export interface DateAndTimeProps {
  dateRegionalSettingsLocale: string;
  totalNumberOfSessions: number;
  slot: FormNestedSlot;
  service: Service;
  isBlackOnly?: boolean;
}
const DateAndTime: React.FC<DateAndTimeProps> = ({
  service,
  totalNumberOfSessions,
  slot,
  dateRegionalSettingsLocale,
  isBlackOnly,
}) => {
  const { t } = useTranslation();
  const editorContext = useEditorContext();
  const { startDate, timezone, endDate } = slot;
  const isCourse = service.type === ServiceType.COURSE;

  const endDateAndTime = shouldShowEndDate(slot, service)
    ? getDateAndTime({
        startDate: endDate,
        dateRegionalSettingsLocale,
        totalNumberOfSessions,
        t,
      })
    : undefined;

  const dateAndTime = editorContext.isDummy
    ? t('app.dummy-data.date-and-time')
    : getDateAndTime({
        startDate: startDate!,
        dateRegionalSettingsLocale,
        totalNumberOfSessions,
        t,
        timezone: isCourse ? timezone : undefined,
      });

  return (
    <div className={classes.root}>
      <Text
        type={TextType.Primary}
        data-hook={DateAndTimeDataHooks.SLOT_DATE_AND_TIME}
        tagName={TagName.Paragraph}
        isBlackOnly={isBlackOnly}
      >
        {dateAndTime}
      </Text>
      {endDateAndTime && (
        <Text
          type={TextType.Primary}
          data-hook={DateAndTimeDataHooks.SLOT_END_DATE_AND_TIME}
          tagName={TagName.Paragraph}
          isBlackOnly={isBlackOnly}
        >
          {endDateAndTime}
        </Text>
      )}
    </div>
  );
};

const getDateAndTime = ({
  startDate,
  dateRegionalSettingsLocale,
  totalNumberOfSessions,
  t,
  timezone,
}: {
  startDate: string;
  dateRegionalSettingsLocale: string;
  totalNumberOfSessions: number;
  t: TFunction;
  timezone?: string;
}) => {
  const dateAndTimeFormatter = new DateTimeFormatter(
    dateRegionalSettingsLocale!,
    timezone,
  );
  const dateAndTimeFormat = dateAndTimeFormatter.formatDateAndTime(startDate);
  const isServiceHasMultipleSessions = totalNumberOfSessions > 1;

  return isServiceHasMultipleSessions
    ? t('app.booking-details.course.date-and-time.starts.text', {
        date: dateAndTimeFormat,
      })
    : dateAndTimeFormat;
};

const shouldShowEndDate = (slot: FormNestedSlot, service: Service) => {
  if (service.type === ServiceType.COURSE) {
    return false;
  }

  const duration =
    new Date(slot.endDate).getTime() - new Date(slot.startDate).getTime();
  return duration / (24 * 60 * 60 * 1000) >= 7;
};

export default DateAndTime;
